import { useState, useEffect, useCallback } from 'react';
import { lazy, Suspense } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation
} from "react-router-dom";
import { WebSocketProvider } from './contexts/WebSocketContext';
import LoginPage from './pages/Login';
import SessionSidebar from './components/SessionSidebar';
import SiteGate from './components/SiteGate';
import ConsentGate from './components/ConsentGate';
import { PENDING_CLAIM_TOKEN_KEY } from './constants/claim';

const BookRecommend = lazy(() => import("./pages/Book"))
const TasteWeaver = lazy(() => import("./pages/TasteWeaver"))
const SharedGallery = lazy(() => import("./pages/SharedGallery"))
const SharedSessionDetail = lazy(() => import("./pages/SharedSessionDetail"))
const About = lazy(() => import("./pages/About"))
const Why = lazy(() => import("./pages/Why"))
const ClaimPage = lazy(() => import("./pages/ClaimPage"))
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"))
const ComposePage = lazy(() => import("./pages/ComposePage"))
const LegalDoc = lazy(() => import("./pages/Legal"))

/**
 * 認証必須ルートのラッパー
 * 未認証時はLoginPageを表示、認証済みの場合はWebSocketProvider + AppLayout でラップ
 */
function AuthenticatedLayout({ isAuthenticated, isChecking, user, useSNSAuth, onLoginSuccess, onLogout, onConsented, children }) {
  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '20px',
        color: '#667eea'
      }}>
        読み込み中...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={onLoginSuccess} />;
  }

  // 未同意ユーザーは本体を覆う遮断ゲートで止める（同意 or ログアウトの二択）
  if (useSNSAuth && user?.needsConsent) {
    return <ConsentGate onConsented={onConsented} onLogout={onLogout} />;
  }

  return (
    <WebSocketProvider>
      {children}
    </WebSocketProvider>
  );
}

/**
 * claim ログイン継続の検出（admin-qr-sessions Section 2.3 課題1）
 *
 * 未ログインで「受け取る」を押したユーザーは OAuth 後に origin/chat へ着地する。
 * Router 配下のこのコンポーネントが、認証チェック完了 + 認証済み確定後に
 * sessionStorage の pendingClaimToken を検出し、/claim/:token へ置換 navigate する。
 * 自動 claim はしない（ClaimPage が「受け取る」ボタンを再提示し明示1クリックで確定）。
 */
function ClaimContinuation({ isAuthenticated, isChecking }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (isChecking || !isAuthenticated) return;
    let token = null;
    try {
      token = sessionStorage.getItem(PENDING_CLAIM_TOKEN_KEY);
    } catch {
      token = null;
    }
    if (!token) return;
    try {
      sessionStorage.removeItem(PENDING_CLAIM_TOKEN_KEY);
    } catch {
      /* noop */
    }
    navigate(`/claim/${token}`, { replace: true });
  }, [isAuthenticated, isChecking, navigate]);

  return null;
}

/**
 * ルート変更時に先頭へスクロールする（全ページ共通）。
 * SPA 遷移は既定でスクロール位置を引き継ぐため、長いページの末尾リンクから
 * 別ページへ移ると途中位置で開いてしまう。pathname 変化時のみ発火するので、
 * 同一ページ内の state 更新（ギャラリーの無限スクロール等）には影響しない。
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [user, setUser] = useState(null); // SNS認証のユーザー情報
  const [useSNSAuth, setUseSNSAuth] = useState(false); // SNS認証モードフラグ
  // サイドバー開閉: 一度ユーザーが選んだ状態を localStorage で永続化。
  // 初回はデスクトップ(>768px)のみ開いて機能を伝える。モバイルは展開が全面オーバーレイ
  // (SessionSidebar.css) でメインを覆うため初回は閉じる。
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) return saved === 'true';
    return window.innerWidth > 768;
  });
  const [restoredSession, setRestoredSession] = useState(null); // 復元セッションデータ

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // 初回マウント時に認証状態をチェック
  useEffect(() => {
    checkAuthStatus();
  }, []); // eslint-disable-line

  const checkAuthStatus = async () => {
    try {
      // まず認証モードをチェック（/api/auth/me）
      // 本番(Vercel)には /api を backend へ転送する rewrite が無く、相対 '/api/...' は
      // SPA フォールバック(index.html)を返して JSON parse が壊れる。LoginPage と同じく
      // backendUrl(Railway) を直叩きし、credentials:'include' でクロスドメイン Cookie を送る。
      const meResponse = await fetch(`${backendUrl}/api/auth/me`, {
        credentials: 'include'
      });

      if (meResponse.ok) {
        const meData = await meResponse.json();

        // サイトパスワードが必要な場合 → 未認証状態
        if (meData.sitePasswordRequired) {
          setIsAuthenticated(false);
          setIsChecking(false);
          return;
        }

        if (meData.authMode === 'sns') {
          // SNS認証モード
          setUseSNSAuth(true);

          if (meData.authenticated && meData.user) {
            // SNS認証済み
            setUser(meData.user);
            setIsAuthenticated(true);
            setIsChecking(false);
            return;
          } else {
            // SNS認証モードだが未認証
            setIsAuthenticated(false);
            setIsChecking(false);
            return;
          }
        }

        // authMode === 'password': 従来型パスワード認証モード
      }
    } catch (error) {
      // バックエンド未起動時のネットワークエラーは抑制
      if (error.name !== 'TypeError') {
        console.error('Auth mode check error:', error);
      }
    }

    // フォールバック: 従来型パスワード認証
    const password = localStorage.getItem('auth-password');
    const timestamp = localStorage.getItem('auth-timestamp');

    if (!password) {
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }

    // 24時間経過チェック
    if (timestamp && Date.now() - parseInt(timestamp) > 24 * 60 * 60 * 1000) {
      localStorage.removeItem('auth-password');
      localStorage.removeItem('auth-timestamp');
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }

    await checkPasswordAuth(password);
  };

  const checkPasswordAuth = async (password) => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/status`, {
        headers: { 'x-auth-password': password },
        credentials: 'include'
      });

      const data = await response.json();
      setIsAuthenticated(data.authenticated);

      if (!data.authenticated) {
        localStorage.removeItem('auth-password');
        localStorage.removeItem('auth-timestamp');
      }
    } catch (error) {
      console.error('Password auth check error:', error);
      setIsAuthenticated(false);
    } finally {
      setIsChecking(false);
    }
  };

  const handleLoginSuccess = useCallback((userData) => {
    if (userData) {
      setUser(userData);
      setUseSNSAuth(true);
    }
    setIsAuthenticated(true);
  }, []);

  // 規約同意の記録成功後、遮断ゲートを解除する（BE が consent を保存済み）
  const handleConsented = useCallback(() => {
    setUser(prev => (prev ? { ...prev, needsConsent: false } : prev));
  }, []);

  const handleLogout = useCallback(async () => {
    if (useSNSAuth) {
      try {
        const res = await fetch(`${backendUrl}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        });
        // 非 OK を握り潰すと、サーバ側セッションが残ったまま直後の /me 自動再ログインで
        // 認証画面に戻れなくなる (BACKLOG bug)。失敗は明示ログしてユーザーには続行。
        if (!res.ok) {
          console.error('Logout request failed with status:', res.status);
        }
      } catch (error) {
        console.error('Logout error:', error);
      }
      setUser(null);
      setUseSNSAuth(false);
    }

    localStorage.removeItem('auth-password');
    localStorage.removeItem('auth-timestamp');
    setIsAuthenticated(false);
    // 明示ログアウト後は公開トップへ hard redirect。/chat に留まると LoginPage の
    // /me 自動再ログインが、サーバ側 logout 失敗時に認証状態へ戻す bounce を起こすため、
    // ページを作り直して状態を持ち越さない。
    window.location.href = '/';
  }, [useSNSAuth, backendUrl]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => {
      const next = !prev;
      localStorage.setItem('sidebarOpen', String(next));
      return next;
    });
  }, []);

  // セッション復元ハンドラ
  const handleRestoreSession = useCallback((sessionData) => {
    setRestoredSession(sessionData);
  }, []);

  // 復元モードをクリアして通常モードに戻る
  const handleClearRestore = useCallback(() => {
    setRestoredSession(null);
  }, []);

  // 管理者ルートの共通ガード（要認証 + isAdmin・WebSocket 不要。非管理者は / へ）
  const adminGuard = (element) =>
    isChecking ? (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '20px', color: '#667eea' }}>
        読み込み中...
      </div>
    ) : !isAuthenticated ? (
      <LoginPage onLoginSuccess={handleLoginSuccess} />
    ) : !user?.isAdmin ? (
      <Navigate to="/" replace />
    ) : (useSNSAuth && user?.needsConsent) ? (
      <ConsentGate onConsented={handleConsented} onLogout={handleLogout} />
    ) : (
      element
    );

  // 認証必須コンテンツのラッパー
  const authProps = {
    isAuthenticated,
    isChecking,
    user,
    useSNSAuth,
    onLoginSuccess: handleLoginSuccess,
    onLogout: handleLogout,
    onConsented: handleConsented,
  };

  return (
    <Router>
      <ScrollToTop />
      <ClaimContinuation isAuthenticated={isAuthenticated} isChecking={isChecking} />
      <Suspense fallback={
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '20px',
          color: '#667eea'
        }}>
          読み込み中...
        </div>
      }>
        <Routes>
          {/* 閲覧トップ / ブラウズ（プレオープン中は SiteGate=babak2026 で閉じる。
              SITE_PASSWORD 未設定なら素通り。SNS ログインは不要） */}
          <Route path="/" element={
            <SiteGate onUnlock={checkAuthStatus}>
              <SharedGallery isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
            </SiteGate>
          } />
          <Route path="/about" element={
            <SiteGate onUnlock={checkAuthStatus}>
              <About isAuthenticated={isAuthenticated} user={user} />
            </SiteGate>
          } />
          {/* なぜBabak?（思想・マニフェスト。/about と同じくゲート内公開） */}
          <Route path="/why" element={
            <SiteGate onUnlock={checkAuthStatus}>
              <Why isAuthenticated={isAuthenticated} user={user} />
            </SiteGate>
          } />
          {/* 直接の共有リンク（公開・認証不要・ゲート不要。受け取った相手がそのまま開ける） */}
          <Route path="/shared/:shareId" element={
            <SharedSessionDetail isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
          } />
          {/* 宝の受け取り（公開・認証不要・ゲート不要・WebSocket不要。claim はログインで実行） */}
          <Route path="/claim/:token" element={
            <ClaimPage isAuthenticated={isAuthenticated} isChecking={isChecking} user={user} />
          } />
          {/* 法務文書（公開・認証不要・SiteGate 不要。未ログインでも読めるべき） */}
          <Route path="/terms" element={<LegalDoc doc="terms" />} />
          <Route path="/privacy" element={<LegalDoc doc="privacy" />} />

          {/* 認証必須ルート */}
          <Route path="/chat" element={
            <AuthenticatedLayout {...authProps}>
              <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
                {useSNSAuth && (
                  <SessionSidebar
                    isOpen={sidebarOpen}
                    onToggle={toggleSidebar}
                    onRestoreSession={handleRestoreSession}
                    onClearRestore={handleClearRestore}
                    activeSessionId={restoredSession?._id || null}
                  />
                )}
                <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
                  <BookRecommend
                    onLogout={handleLogout}
                    user={user}
                    onToggleSidebar={useSNSAuth ? toggleSidebar : null}
                    restoredSession={restoredSession}
                    onClearRestore={handleClearRestore}
                    onRestoreSession={handleRestoreSession}
                  />
                </div>
              </div>
            </AuthenticatedLayout>
          } />
          <Route path="/tasteweaver" element={
            <AuthenticatedLayout {...authProps}>
              <TasteWeaver />
            </AuthenticatedLayout>
          } />
          {/* 管理画面（要認証 + isAdmin。WebSocket 不要。非管理者は / へ） */}
          <Route path="/admin" element={
            adminGuard(<AdminDashboard isAuthenticated={isAuthenticated} user={user} />)
          } />
          {/* 会話結果から作成（compose・管理者専用サブビュー） */}
          <Route path="/admin/compose" element={
            adminGuard(<ComposePage isAuthenticated={isAuthenticated} user={user} />)
          } />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
